import CtaSecondary from "components/CtaSecondary";
import TokenIcon from "components/icons/TokenIcon";
import { BasicProps } from "components/types/BasicProps";
import { ICurrency } from "logic/types/currency/ICurrency";
import { FC, MouseEvent, useMemo, useState } from "react";
import { accountLiterals } from "../literals";
import CtaPrimary from "components/CtaPrimary";
import { RiArrowDownSLine } from "react-icons/ri";
import classNames from "classnames";
import { useModalQr, useTokenBalances } from "../AccountProvider";
import useModalHelper from "components/modals/hooks/useModalHelper";
import { ModalSendTokens } from "./ModalSendTokens";
import unlockdWalletModule from "logic/UnlockdWalletModule";
import { ITransfer } from "logic/types/transfers/ITransfer";
import { handleErrors } from "errors";
import { useErrorAlert } from "logic/hooks";
import { Transfers } from "./Transfers";
import { useUpdateEffect } from "react-use";
import track from "logic/track";

type Props = BasicProps & {
  token: ICurrency;
  onTransactionDone: () => void;
  refreshTransactions: boolean;
};

const { receiveCta, sendCta } = accountLiterals.tokenRow;

export const TokenRow: FC<Props> = (props) => {
  const { token, onTransactionDone, refreshTransactions } = props;
  const [error, setError] = useState<Error | null>(null);
  useErrorAlert(error);
  const [isRowOpened, setRowOpen] = useState<boolean>(false);
  const [isSendTokensModalOpen, toggleModalSendTokens] = useModalHelper();
  const [transfers, setTransfers] = useState<ITransfer[]>();
  const [loadingTransfers, setLoadingTransfers] = useState<boolean>(false);
  const tokenBalances = useTokenBalances();

  const balance = useMemo<bigint>(
    () => tokenBalances.get(token.name)!,
    [tokenBalances]
  );

  const [, toggleQrModal] = useModalQr();

  useUpdateEffect(() => {
    if (transfers !== undefined) {
      handleGetTransactionsHistory();
    }
  }, [refreshTransactions]);

  const handleGetTransactionsHistory = async () => {
    try {
      setLoadingTransfers(true);

      const transfers = await unlockdWalletModule.getAssetTransfers(token);

      setTransfers(transfers);
    } catch (err) {
      setError(handleErrors(err));
    } finally {
      setLoadingTransfers(false);
    }
  };

  const toggleOpenRow = () => {
    if (transfers === undefined && !loadingTransfers) {
      handleGetTransactionsHistory();
    }

    setRowOpen(!isRowOpened);
  };

  const arrowStyles = useMemo<string>(
    () => (isRowOpened ? "rotate-180" : "rotate-0"),
    [isRowOpened]
  );

  const handleReceiveClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    track.click(`account_tokens_${token.name}_click_receive`)

    toggleQrModal();
  };

  const handleSendClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    track.click(`account_tokens_${token.name}_click_send`)

    toggleModalSendTokens();
  };

  return (
    <>
      <li
        className="bg-dark rounded-[10px] py-[21px] pl-[33px] pr-[58px] flex flex-col cursor-pointer"
        onClick={toggleOpenRow}
      >
        <div className="w-full flex items-center justify-between gap-4">
          <div className="w-full flex items-center gap-[15px]">
            <TokenIcon
              className="w-[50px] h-[50px]"
              currency={token}
              secondary={true}
            />
            <div className="flex flex-col">
              <span className="font-bold">{token.label}</span>
              <span className="font-medium">{`${token.formatAmount(balance)} ${token.label
                }`}</span>
            </div>
          </div>
          <div className="flex flex-end items-center">
            <CtaSecondary className="w-[130px]" onClick={handleReceiveClick}>
              {receiveCta}
            </CtaSecondary>
            <CtaPrimary
              className="ml-[15px] w-[130px]"
              onClick={handleSendClick}
            >
              {sendCta}
            </CtaPrimary>
            <RiArrowDownSLine
              className={classNames(
                "ml-[20px] w-6 h-6 transition-transform duration-200 transform",
                arrowStyles
              )}
            />
          </div>
        </div>
        {isRowOpened && (
          <Transfers
            className="mt-[52px] w-full flex flex-col"
            transfers={transfers}
          />
        )}
      </li>
      {isSendTokensModalOpen && (
        <ModalSendTokens
          isOpen={isSendTokensModalOpen}
          toggleModal={toggleModalSendTokens}
          defaultTokenSelected={token}
          onTransactionDone={onTransactionDone}
        />
      )}
    </>
  );
};
